import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, GraduationCap, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PRODUCTS } from "@/config/products";

export function LandingPricing() {
  const [loadingProduct, setLoadingProduct] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<Record<string, "month" | "year">>({
    soulsafe: "month",
    soulsafe_leader: "month",
  });
  const { user, subscription } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubscribe = async (productId: string, priceId: string, tierName: string) => {
    if (!user) { navigate("/auth"); return; }
    setLoadingProduct(`${productId}-${tierName}`);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", { body: { priceId } });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to start checkout", variant: "destructive" });
    } finally {
      setLoadingProduct(null);
    }
  };

  const soulsafe = PRODUCTS.soulsafe;
  const leader = PRODUCTS.soulsafe_leader;

  const getActiveTier = (product: typeof soulsafe, interval: "month" | "year") =>
    product.tiers.find((t) => t.interval === interval) || product.tiers[0];

  const ssTier = getActiveTier(soulsafe, billingInterval.soulsafe);
  const ldTier = getActiveTier(leader, billingInterval.soulsafe_leader);

  const cards = [
    {
      key: "soulsafe",
      title: "Soul Safe Sanctuary™",
      subtitle: "For Survivors & Members",
      originalPrice: billingInterval.soulsafe === "month" ? "$9.99" : "$59.90",
      price: ssTier.price,
      interval: ssTier.interval,
      features: ssTier.features,
      priceId: ssTier.priceId,
      tierName: ssTier.name,
      productId: "soulsafe",
      badge: null,
      icon: <GraduationCap className="w-6 h-6" />,
      certTitle: "Soul Safe Sanctuary™ Graduate",
    },
    {
      key: "soulsafe_leader",
      title: "Certified Safe Leader",
      subtitle: "For Church & Ministry Leaders",
      originalPrice: billingInterval.soulsafe_leader === "month" ? "$15.99" : "$99.90",
      price: ldTier.price,
      interval: ldTier.interval,
      features: ldTier.features,
      priceId: ldTier.priceId,
      tierName: ldTier.name,
      productId: "soulsafe_leader",
      badge: "Most Popular",
      icon: <Shield className="w-6 h-6" />,
      certTitle: "Trained Soul Safe Sanctuary™ Leader",
    },
  ];

  return (
    <section className="py-32 bg-card">
      <div className="container px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center mb-20"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-6">
            Simple, Transparent Pricing
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl font-black text-foreground mb-6 leading-[1.05] tracking-[-0.02em]" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span className="relative inline-block">
              <span className="relative z-10">Choose</span>
              <svg
                className="absolute -bottom-2 left-0 w-full z-0"
                viewBox="0 0 200 8"
                fill="none"
                style={{ overflow: 'visible' }}
              >
                <path
                  d="M2 5C40 2 80 6 100 4C120 2 160 6 198 3"
                  stroke="hsl(0, 84%, 60%)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="animate-draw-line"
                />
              </svg>
            </span>{" "}
            Your Path
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {cards.map((card, i) => {
            const loadingKey = `${card.productId}-${card.tierName}`;
            const isLoading = loadingProduct === loadingKey;
            const intervalKey = card.key as "soulsafe" | "soulsafe_leader";
            const currentInterval = billingInterval[intervalKey];

            return (
              <motion.div
                key={card.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                {card.badge && (
                  <div className="absolute -top-3 right-6 z-10">
                    <span className="bg-foreground text-background text-xs font-bold px-4 py-1.5 rounded-full">
                      {card.badge}
                    </span>
                  </div>
                )}

                <div className={`h-full border p-8 md:p-10 bg-background transition-all ${
                  card.badge ? "border-foreground/30 shadow-[var(--shadow-elevated)]" : "border-border"
                }`}>
                  {/* Icon + Title */}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                      {card.icon}
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold text-foreground">{card.title}</h3>
                    </div>
                  </div>
                  <motion.p
                    className="text-sm text-destructive font-semibold mb-6 ml-[52px]"
                    animate={{ opacity: [0.65, 1, 0.65], x: [0, 2, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
                  >
                    {card.subtitle}
                  </motion.p>

                  {/* Billing Toggle */}
                  <div className="flex bg-muted p-1 mb-6">
                    <button
                      onClick={() => setBillingInterval((prev) => ({ ...prev, [intervalKey]: "month" }))}
                      className={`flex-1 text-sm font-medium py-2.5 px-4 transition-all ${
                        currentInterval === "month"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingInterval((prev) => ({ ...prev, [intervalKey]: "year" }))}
                      className={`flex-1 text-sm font-medium py-2.5 px-4 transition-all ${
                        currentInterval === "year"
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Yearly
                    </button>
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 mb-8">
                    <span className="text-sm text-muted-foreground line-through">{card.originalPrice}</span>
                    <span className="text-4xl font-bold text-foreground">
                      ${card.price.toFixed(2)}
                    </span>
                    <span className="text-muted-foreground">/{currentInterval === "month" ? "mo" : "yr"}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {card.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button
                    onClick={() => handleSubscribe(card.productId, card.priceId, card.tierName)}
                    disabled={isLoading || subscription.subscribed}
                    className="w-full h-14 text-base font-bold rounded-full"
                    size="lg"
                  >
                    {isLoading ? (
                      "Processing..."
                    ) : subscription.subscribed ? (
                      "Currently Subscribed"
                    ) : (
                      <>
                        Start Free Trial
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Cert badge */}
                  <div className="mt-6 flex items-center gap-3 border-t border-border pt-6">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent shrink-0">
                      {card.icon}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Earn your</p>
                      <p className="text-sm font-semibold text-foreground">{card.certTitle}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-wrap items-center justify-center gap-8 mt-12 text-sm text-muted-foreground"
        >
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-accent" /> 7-day free trial
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-accent" /> Cancel anytime
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-accent" /> Secure payment via Stripe
          </span>
        </motion.div>
      </div>
    </section>
  );
}
